import { Component, DestroyRef, Input, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { DateRangePickerComponent as KitDateRangePickerComponent } from '@shared/components/date-range-picker';

import type { Subscription } from 'rxjs';
import type { WritableSignal } from '@angular/core';
import type { DateRangeSelection } from '@shared/utils';

/**
 * Adaptateur entre l'API historique de BackStage (deux `FormControl` de dates) et
 * le `app-date-range-picker` du design system, qui raisonne en `DateRangeSelection`.
 *
 * Écrit comme un adaptateur plutôt qu'en migrant les appelants : les trois
 * consommateurs (`evenement-filtre`, `formules-filtre`, `fichier-sap`) gardent
 * `[startDate]` / `[endDate]` inchangés, et la sortie de moment reste confinée ici.
 */
@Component({
  // Renommé : le composant du kit occupe déjà `app-date-range-picker`.
  selector: 'app-plage-dates',
  imports: [KitDateRangePickerComponent],
  templateUrl: './date-range-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './date-range-picker.component.scss',
})
export class DateRangePickerComponent {
  @Input({ required: true }) set startDate(control: FormControl) {
    this.brancher(this.debut, control);
  }

  @Input({ required: true }) set endDate(control: FormControl) {
    this.brancher(this.fin, control);
  }

  private readonly debut = signal<FormControl | null>(null);
  private readonly fin = signal<FormControl | null>(null);

  /** Version signal des valeurs, pour que la sélection se recalcule sur reset. */
  private readonly revision = signal(0);

  private readonly abonnements = new Map<WritableSignal<FormControl | null>, Subscription>();

  constructor() {
    inject(DestroyRef).onDestroy(() => this.desabonnerTout());
  }

  /**
   * Aucune date saisie ⇒ `all`, et non `upcoming`.
   *
   * `upcoming` affichait « À venir » sur le déclencheur alors que rien ne filtrait :
   * `appliquerSelection` ne pose des bornes que sur `custom`, donc le formulaire
   * partait sans date et le backend rendait tout. Le libellé annonçait un filtre que
   * personne n'appliquait. Le rail de raccourcis étant masqué (`[presets]="[]"` dans
   * le template), l'utilisateur ne pouvait même pas choisir `upcoming` : c'était un
   * pur artefact de ce repli.
   *
   * `all` est l'état de repos du kit — `clearsTo` y vaut `{ preset: 'all' }` par
   * défaut, et son `valueText` rend `null` sur `all`, donc le déclencheur réaffiche
   * son libellé au lieu d'une valeur. Le champ se lit « sans sélection », ce qu'il est.
   */
  protected readonly selection = computed<DateRangeSelection>(() => {
    this.revision();
    const from = (this.debut()?.value as string) || null;
    const to = (this.fin()?.value as string) || null;
    return from || to ? { preset: 'custom', from, to } : { preset: 'all' };
  });

  protected appliquerSelection(selection: DateRangeSelection): void {
    const from = selection.preset === 'custom' ? selection.from : null;
    const to = selection.preset === 'custom' ? selection.to : null;
    this.debut()?.setValue(from ?? '');
    this.fin()?.setValue(to ?? '');
    this.revision.update(n => n + 1);
  }

  clearDate(): void {
    this.debut()?.reset();
    this.fin()?.reset();
    this.revision.update(n => n + 1);
  }

  /**
   * Un `FormControl` n'est pas un signal : `selection` ne peut pas dépendre de sa
   * valeur. Sans cet abonnement, un `setValue()`, `patchValue()` ou `reset()` venu du
   * PARENT laisse la plage précédente affichée — c'est le cas du « Réinitialiser les
   * filtres » d'`evenement-filtre` et de `formules-filtre`, qui appellent
   * `filterForm.reset()` sans passer par `clearDate()`.
   *
   * L'ancien abonnement est coupé quand le contrôle est remplacé : les setters sont
   * des `@Input`, un parent peut donc en fournir un autre à tout moment.
   */
  private brancher(emplacement: WritableSignal<FormControl | null>, control: FormControl): void {
    this.abonnements.get(emplacement)?.unsubscribe();
    emplacement.set(control);
    this.abonnements.set(
      emplacement,
      control.valueChanges.subscribe(() => this.revision.update(n => n + 1)),
    );
  }

  private desabonnerTout(): void {
    this.abonnements.forEach(abonnement => abonnement.unsubscribe());
    this.abonnements.clear();
  }
}
